// ── 매입 붙여넣기 미리보기 모달 ──────────────────────────────────────────────
// 스프레드시트에서 복사한 정리본(구분/사용일/업체/금액/비고/비고2/세금계산서/발급일)을
// 첫 셀에 Ctrl+V → 표로 미리보기. 행별 상태: 신규 / 갱신(이미 있음→덮어씀) / 오류.
// 저장 시 신규+갱신 행(오류 제외) 전송 → 서버가 핵심 4필드 멀티셋 병합.

import React, { useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import {
  PASTE_COLUMNS, emptyRow, isRowEmpty, normText,
  parseClipboardMatrix, isHeaderRow, matrixToRows, rowError, buildStatuses,
} from './pasteUtils';

const STATUS_BADGE = {
  new:    { label: '신규', color: '#15803d', bg: 'rgba(34,197,94,0.15)' },
  update: { label: '갱신', color: '#b45309', bg: 'rgba(245,158,11,0.16)' },
  error:  { label: '오류', color: '#991b1b', bg: 'rgba(239,68,68,0.15)' },
  empty:  { label: '',     color: 'var(--text-muted)', bg: 'transparent' },
};

const INITIAL_ROWS = 6;

export default function PastePurchaseModal({ open, title, existingRows, onClose, onSave }) {
  const [rows, setRows] = useState(() => Array.from({ length: INITIAL_ROWS }, emptyRow));
  const [saving, setSaving] = useState(false);

  const { statuses, summary } = useMemo(() => buildStatuses(rows, existingRows), [rows, existingRows]);
  const canSave = !saving && summary.errorCount === 0 && (summary.newCount + summary.updateCount) > 0;

  function reset() {
    setRows(Array.from({ length: INITIAL_ROWS }, emptyRow));
  }

  function setCell(i, key, val) {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[i] = { ...next[i], [key]: val };
      return next;
    });
  }

  function handlePaste(e, startRow, startCol) {
    const text = e.clipboardData.getData('text/plain');
    const matrix = parseClipboardMatrix(text);
    if (!matrix.length) return;
    e.preventDefault();
    // 헤더행 포함 블록 → 컬럼 자동 매핑 후 전체 교체
    if (isHeaderRow(matrix[0])) {
      const mapped = matrixToRows(matrix);
      setRows(mapped.length ? [...mapped, emptyRow(), emptyRow()] : [emptyRow()]);
      return;
    }
    // 그 외 → 시작 셀부터 확산 (부족한 행 자동 추가)
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      while (next.length < startRow + matrix.length) next.push(emptyRow());
      matrix.forEach((cells, rOff) => {
        cells.forEach((val, cOff) => {
          const col = PASTE_COLUMNS[startCol + cOff];
          if (!col) return;
          next[startRow + rOff] = { ...next[startRow + rOff], [col.key]: normText(val) };
        });
      });
      return next;
    });
  }

  function addRows(n = 10) {
    setRows((prev) => [...prev, ...Array.from({ length: n }, emptyRow)]);
  }
  function trimEmpty() {
    setRows((prev) => {
      const filtered = prev.filter((r) => !isRowEmpty(r));
      return filtered.length ? filtered : [emptyRow()];
    });
  }

  async function doSave() {
    const payload = rows.filter((r) => !isRowEmpty(r) && rowError(r) === '');
    if (!payload.length) return;
    setSaving(true);
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) { reset(); onClose(); }
  }

  function handleClose() {
    if (saving) return;
    reset();
    onClose();
  }

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: 'var(--text-sub)' }}>
        신규 <strong style={{ color: '#15803d' }}>{summary.newCount}</strong>
        {' · '}갱신 <strong style={{ color: '#b45309' }}>{summary.updateCount}</strong>
        {summary.errorCount > 0 && <> {' · '}오류 <strong style={{ color: '#dc2626' }}>{summary.errorCount}</strong></>}
      </span>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <button style={btnGhost} onClick={handleClose} disabled={saving}>취소</button>
        <button style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}
          onClick={doSave} disabled={!canSave}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal open={open} icon="📋" title={title} onClose={handleClose} footer={footer} width="min(1120px, calc(100vw - 32px))">
      <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 8, lineHeight: 1.6 }}>
        스프레드시트에서 복사한 표를 아래 <strong>첫 칸을 클릭한 뒤 Ctrl+V</strong> 하세요(머리글 줄 포함해도 됩니다).
        같은 거래(구분·사용일·업체·금액)가 이미 있으면 <strong>비고·세금계산서·발급일을 덮어써 갱신</strong>하고, 없으면 새로 추가합니다.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button style={btnGhost} onClick={() => addRows(10)}>행 10개 추가</button>
        <button style={btnGhost} onClick={trimEmpty}>빈 행 정리</button>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, width: '100%', minWidth: 940 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 34, textAlign: 'center' }}>#</th>
              {PASTE_COLUMNS.map((c) => (
                <th key={c.key} style={{ ...thStyle, width: c.width, textAlign: c.align || 'left' }}>{c.label}</th>
              ))}
              <th style={{ ...thStyle, width: 96, textAlign: 'center' }}>상태</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const st = statuses[i] || { status: 'empty', message: '' };
              const badge = STATUS_BADGE[st.status] || STATUS_BADGE.empty;
              const isErr = st.status === 'error';
              return (
                <tr key={i} style={{ background: isErr ? 'rgba(239,68,68,0.06)' : 'transparent' }}>
                  <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>{i + 1}</td>
                  {PASTE_COLUMNS.map((c, ci) => (
                    <td key={c.key} style={tdStyle}>
                      <input
                        value={row[c.key] || ''}
                        onChange={(e) => setCell(i, c.key, e.target.value)}
                        onPaste={(e) => handlePaste(e, i, ci)}
                        style={{ ...cellInput, textAlign: c.align || 'left' }}
                      />
                    </td>
                  ))}
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {st.status !== 'empty' && (
                      <span title={st.message || ''} style={{
                        fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 10,
                        color: badge.color, background: badge.bg,
                      }}>{badge.label}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

const thStyle = { padding: '6px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-sub)', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', top: 0 };
const tdStyle = { padding: 0, borderBottom: '1px solid var(--border)' };
const cellInput = { width: '100%', padding: '4px 6px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit', outline: 'none' };
const btnPrimary = { padding: '6px 16px', border: 'none', borderRadius: 6, background: 'var(--accent, #2563eb)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 12, cursor: 'pointer' };
